/**
 * Section papers: standalone single-section papers (UCAT Decision Making only,
 * TARA Problem Solving only, TMUA Paper 1 only or Paper 2 only, ESAT Mathematics 1 only).
 *
 * They are their own quiz_papers category with ids in the 3000 range
 * (UCAT-3001-s2, TMUA-3001-p1, ESAT-3001-sA), and carry a tier (foundations or
 * beyond) only so that access follows the plan. They are never a mock, never
 * paired into a sitting (each is a set of one part, so catalogueSittings never joins
 * UCAT-3001-s2 and UCAT-3001-s3 as two sections of one mock), and never a past paper.
 *
 * The category decides, whatever the id or tier looks like: a section paper with
 * an id shaped like a mock bank is still a section paper, and a mock whose id
 * happens to be in the 3000 range is still a mock.
 */

import { ESAT_MODULE_LABELS } from './classifyEsatModule';
import { TARA_MODULE_LABELS } from './classifyTaraPaperModule';
import { UCAT_SUBTEST_LABELS } from './ucatSubtests';
import { topicAttemptRowFromMap, topicRowAction } from './topicAttemptRow';

export const SECTION_PAPERS_CATEGORY = 'section_papers';
export const SECTION_PAPERS_HEADING = 'Section practice';

// A section paper with no tier fails closed to the lower paid tier rather than
// open to every plan, as the backend files a paper it has no row for.
export const SECTION_PAPER_DEFAULT_TIER = 'foundations';

const TIER_ORDER = { foundations: 1, beyond: 2 };

// The tiers a section paper can be filed under on the workspace's Practice Topics pages,
// the same three a mock is filed under on the Mock Tests pages.
export const SECTION_PAPER_TIERS = ['beginnings', 'foundations', 'beyond'];

export function isSectionPaperEntry(paper) {
  return String(paper?.category || '').trim().toLowerCase() === SECTION_PAPERS_CATEGORY;
}

function paperKey(paper) {
  return String(paper?.id || paper?.catalog_id || '').trim().toLowerCase();
}

/** The paper's own title: section papers are not "Mock N". */
export function sectionPaperTitle(paper) {
  return String(paper?.display_name || paper?.title || paper?.id || paper?.catalog_id || '').trim();
}

/** "Decision Making" for "Decision Making: Section Paper 13 (harder)"; '' when the title has no section. */
export function sectionPaperGroupLabel(paper) {
  const title = sectionPaperTitle(paper);
  const at = title.indexOf(':');
  return at > 0 ? title.slice(0, at).trim() : '';
}

function idSuffix(paper) {
  const match = paperKey(paper).match(/-([a-z0-9]+)$/);
  return match ? match[1] : '';
}

function paperNumber(paper) {
  const fromTitle = sectionPaperTitle(paper).match(/Section Paper\s+(\d+)/i);
  if (fromTitle) return parseInt(fromTitle[1], 10);
  const fromId = paperKey(paper).match(/-(\d{4})-/);
  return fromId ? parseInt(fromId[1], 10) : Number.MAX_SAFE_INTEGER;
}

function dedupeScore(paper) {
  let score = 0;
  if (paper?.id && paper.id === paper.catalog_id) score += 10;
  score += Math.min(Number(paper?.question_count) || 0, 50);
  return score;
}

/**
 * One entry per section paper id. Keyed on the id alone, never on a mock slot:
 * the mock de-duplication reads the digits of an id as a mock number.
 */
export function dedupeSectionPapers(papers) {
  const kept = new Map();
  (papers || []).forEach((paper) => {
    const key = paperKey(paper);
    if (!key) return;
    const prev = kept.get(key);
    if (!prev || dedupeScore(paper) > dedupeScore(prev)) kept.set(key, paper);
  });
  return Array.from(kept.values());
}

/**
 * The Practice Topics page a section paper is listed on: its own `tier`, as Mock Tests
 * files a mock by its `tier`. The standard papers (1 to 12) are `foundations` and the
 * harder ones (13 to 16, titled "(harder)") are `beyond`; the title is not read. A
 * missing or unknown tier is filed under the default tier, so every paper is on
 * exactly one page.
 */
export function sectionPaperTier(paper) {
  const tier = String(paper?.tier || '').trim().toLowerCase();
  return SECTION_PAPER_TIERS.includes(tier) ? tier : SECTION_PAPER_DEFAULT_TIER;
}

/** The section papers listed on one tier's Practice Topics page, in the order given. */
export function sectionPapersOfTier(papers, tier) {
  return (papers || []).filter((paper) => sectionPaperTier(paper) === tier);
}

/**
 * How far a student is through some section papers: { done, total, pct, score }.
 *
 * "Completed" is decided as the section paper table decides its Status column: the
 * attempt status from the server, or the workspace's own completed list. A section has
 * no exam-scale score, so `score` is the mean percentage correct over the completed
 * papers that report a mark, or null when none do.
 */
export function sectionPaperProgress(papers, dbPapers = {}, completedList = []) {
  const list = papers || [];
  let done = 0;
  const percents = [];
  list.forEach((paper) => {
    const row = topicAttemptRowFromMap(dbPapers, paper.id);
    if (topicRowAction(row, completedList.includes(paper.id)).status !== 'done') return;
    done += 1;
    const total = Number(row?.score_total) || Number(row?.score_answered) || 0;
    const correct = Number(row?.score_correct);
    if (total > 0 && Number.isFinite(correct)) percents.push((correct / total) * 100);
  });
  return {
    done,
    total: list.length,
    pct: list.length ? Math.round((done / list.length) * 100) : 0,
    score: percents.length ? Math.round(percents.reduce((a, b) => a + b, 0) / percents.length) : null,
  };
}

/** Section by section (the id's own suffix), then by paper number, easier tier first. */
export function sortSectionPapers(papers) {
  return [...(papers || [])].sort((a, b) => {
    const section = idSuffix(a).localeCompare(idSuffix(b), undefined, { numeric: true });
    if (section !== 0) return section;
    const number = paperNumber(a) - paperNumber(b);
    if (number !== 0) return number;
    const tier = (TIER_ORDER[a?.tier] || 99) - (TIER_ORDER[b?.tier] || 99);
    if (tier !== 0) return tier;
    return paperKey(a).localeCompare(paperKey(b));
  });
}

/** The section papers of a /set-papers list, in display order. Empty when there are none. */
export function selectSectionPapers(papers, format = (paper) => paper) {
  const own = (Array.isArray(papers) ? papers : []).filter(isSectionPaperEntry);
  return sortSectionPapers(dedupeSectionPapers(own.map(format)));
}

/** [{ label, papers }] in display order; one unlabelled group when no title names a section. */
export function groupSectionPapers(papers) {
  const groups = [];
  (papers || []).forEach((paper) => {
    const label = sectionPaperGroupLabel(paper);
    let group = groups.find((g) => g.label === label);
    if (!group) {
      group = { label, papers: [] };
      groups.push(group);
    }
    group.papers.push(paper);
  });
  return groups;
}

/**
 * The module a section paper is: the one whose label its title names before
 * the colon ("Decision Making: Section Paper 13 (harder)"). Null when the
 * title names none of `labels` (moduleKey -> label).
 */
function sectionPaperModule(paper, labels) {
  const label = sectionPaperGroupLabel(paper).toLowerCase();
  if (!label) return null;
  const hit = Object.entries(labels).find(([, name]) => name.toLowerCase() === label);
  return hit ? hit[0] : null;
}

// TARA's writing task is not offered as a section paper; it keeps the existing start path.
const TARA_SECTION_MODULES = ['critical_thinking', 'problem_solving'];

const SECTION_PAPER_SITTING_EXAMS = ['ESAT', 'TARA', 'UCAT', 'TMUA'];

/**
 * Which TMUA paper a section paper is, 1 or 2, read from its own id (TMUA-3003-p2),
 * never from its title. Null when the id names neither.
 */
export function tmuaSectionPaperNumber(paper) {
  const match = String(paper?.id || paper?.catalog_id || '').trim().match(/[-_]p([12])$/i);
  return match ? Number(match[1]) : null;
}

/**
 * The "sitting" a section paper is started as, passed to onStartSetPaper where a mock
 * passes its paired sitting. Null means the exam's section papers start as before.
 *
 * ESAT, TARA and UCAT open their Pearson player only for a source marked `pearsonSitting`,
 * and a section paper is one module of that player's sitting (Mathematics 1 or 2 for
 * ESAT, Critical Thinking or Problem Solving for TARA, one of the four subtests for
 * UCAT). So the descriptor names the module (`sectionModule`) and carries no other,
 * which is what stops the ESAT picker asking for further modules, TARA loading the
 * other two and UCAT loading the other three subtests. Timing is the paper's own
 * (`durationMinutes`), not a fixed 40 minutes and not the 90 the start endpoint
 * answers when it knows nothing of the exam.
 *
 * UCAT's mock sitting is `{ pearsonSitting, ucatSiblings }`, and the same shape is used
 * here with `ucatSiblings` holding only this paper, under its own subtest.
 *
 * TMUA always opens its Pearson player, and a TMUA paper started with no sitting is
 * joined to its Paper 2 (TMUA-3003-p1 then TMUA-3003-p2), because a TMUA mock or past
 * paper is one sitting of Paper 1 then Paper 2. A section paper is one paper on its
 * own, so the descriptor says so (`sectionPaper`) and names which paper it is
 * (`paperNumber`, from its own id), and carries no Paper 2 to load.
 */
export function sectionPaperSitting(exam, paper) {
  const examName = String(exam || '').toUpperCase();
  if (!paper || !SECTION_PAPER_SITTING_EXAMS.includes(examName)) return null;
  const minutes = Number(paper.duration_minutes);
  const single = {
    sectionPaper: true,
    ...(Number.isFinite(minutes) && minutes > 0 ? { durationMinutes: minutes } : {}),
  };
  if (examName === 'TMUA') {
    return { ...single, paperNumber: tmuaSectionPaperNumber(paper) };
  }
  const base = { pearsonSitting: true, ...single };
  if (examName === 'ESAT') {
    const sectionModule = sectionPaperModule(paper, ESAT_MODULE_LABELS);
    if (!sectionModule) return null;
    return { ...base, sectionModule, esatOptionals: {} };
  }
  if (examName === 'UCAT') {
    const sectionModule = sectionPaperModule(paper, UCAT_SUBTEST_LABELS);
    if (!sectionModule) return null;
    return {
      ...base,
      sectionModule,
      title: sectionPaperTitle(paper),
      ucatSiblings: { [sectionModule]: paper },
    };
  }
  const sectionModule = sectionPaperModule(paper, TARA_MODULE_LABELS);
  if (!TARA_SECTION_MODULES.includes(sectionModule)) return null;
  return { ...base, sectionModule, taraModules: { [sectionModule]: paper } };
}
